import { useCallback, useRef, useState } from 'react'
import type { EligibilityFormData } from '../../../../components/eligibilitySteps/types'
import type { EnrollmentFieldEditability } from '../../../../utils/enrollmentHydration'
import type { NavigateFn } from '../../types'
import { getInitialFormData } from '../../utils/storage'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'

type UseFlowRuntimeStateArgs = {
  initialStep: number | null
  navigate: NavigateFn
  persistedCurrentStep: number | null
  setPersistedCurrentStep: (step: number) => void
}

export function useFlowRuntimeState({
  initialStep,
  navigate,
  persistedCurrentStep,
  setPersistedCurrentStep
}: UseFlowRuntimeStateArgs) {
  const [currentStep, setCurrentStep] = useState(
    initialStep ?? persistedCurrentStep ?? SELF_ENROLLMENT_STEPS.ELIGIBILITY
  )
  const [formData, setFormData] = useState<EligibilityFormData>(getInitialFormData)
  const [fieldEditability, setFieldEditability] = useState<EnrollmentFieldEditability>({})
  const formDataRef = useRef(formData)
  const [stepSubmissionError, setStepSubmissionError] = useState<string | null>(null)
  const [stepValidationError, setStepValidationError] = useState<string | null>(null)
  const [isCheckingStepAccess, setIsCheckingStepAccess] = useState(false)
  const stepContentRef = useRef<HTMLDivElement | null>(null)
  const checkingStepRef = useRef<HTMLDivElement | null>(null)
  const validationMessageRef = useRef<HTMLDivElement | null>(null)

  const resetAnonymousEnrollmentAndReturnToOptions = useCallback(() => {
    const nextFormData = getInitialFormData()
    formDataRef.current = nextFormData
    setFormData(nextFormData)
    setCurrentStep(SELF_ENROLLMENT_STEPS.ELIGIBILITY)
    navigate('/application', {
      state: {
        stayOnApplication: true
      }
    })
  }, [navigate])

  const updateFormData = useCallback((data: Partial<EligibilityFormData>) => {
    setFormData(previous => ({ ...previous, ...data }))
  }, [])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])

  const goToStep = useCallback(
    (step: number) => {
      setStepSubmissionError(null)
      setStepValidationError(null)
      setCurrentStep(step)
      setPersistedCurrentStep(step)

      if (step !== currentStep && step !== SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE) {
        scrollToTop()
      }
    },
    [currentStep, scrollToTop, setPersistedCurrentStep]
  )

  return {
    checkingStepRef,
    currentStep,
    fieldEditability,
    formData,
    formDataRef,
    goToStep,
    isCheckingStepAccess,
    resetAnonymousEnrollmentAndReturnToOptions,
    scrollToTop,
    setCurrentStep,
    setFieldEditability,
    setFormData,
    setIsCheckingStepAccess,
    setStepSubmissionError,
    setStepValidationError,
    stepContentRef,
    stepSubmissionError,
    stepValidationError,
    updateFormData,
    validationMessageRef
  }
}
